import { cx } from '@emotion/css';
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { MotifComponent, m, useMotifStyles } from '../../motif';
import type { BaseComponentProps } from '../../types';
import { DirectionalOverlay } from '../DirectionalOverlay/DirectionalOverlay';
import { Icon } from '../Icon';
import {
  sideOverlayBackdropMobileOnlyCss,
  sideOverlayCloseButtonCss,
  sideOverlayCss,
  sideOverlayDefinitionCss,
  sideOverlayDirectionLeftCss,
  sideOverlayDirectionRightCss,
  sideOverlayHeaderCss,
  sideOverlayListCss,
  sideOverlayTermCss,
  sideOverlayWithSideNavCss,
  sideOverlayWithSideNavDirectionLeftCss,
} from './SideOverlay.styles';

/**
 * A fixed panel that slides in from the left or right edge of the screen.
 *
 * Use `SideOverlay` when you need an opinionated, content-ready drawer with a built-in close
 * button, title/body layout, Motif theming, RTL support, and mobile-first behaviour
 * (slides from the bottom on mobile, from the side on desktop).
 *
 * Prefer {@link DirectionalOverlay} instead when you need:
 * - A custom slide direction other than `left`/`right` (e.g. `top` or `bottom` on desktop).
 * - Full control over the panel's interior content without a fixed header/body structure.
 * - A different desktop size or a backdrop that is always visible (not mobile-only).
 * - Independent desktop and mobile directions.
 *
 * @example
 * // Standard usage – slides in from the right
 * <SideOverlay isOpen={isOpen} title="Details" body={<p>…</p>} onClose={close} />
 *
 * @example
 * // Left-side drawer alongside a side navigation
 * <SideOverlay isOpen={isOpen} direction="left" hasSideNav title="Menu" onClose={close} />
 */
export type SideOverlayDirection = 'left' | 'right';

/** Desktop panel width is capped in CSS; this sets the animated width basis (viewport share). */
const sideOverlayDesktopSizePercent = 24;
const sideOverlayMobileSizePercent = 100;

interface SideOverlayProps extends BaseComponentProps {
  isOpen: boolean;
  title?: ReactNode;
  body?: ReactNode;
  onClose?: (e: MouseEvent | KeyboardEvent) => void;
  hasSideNav?: boolean;
  /** Which side the overlay slides in from on desktop. Defaults to `'right'`. */
  direction?: SideOverlayDirection;
}

export const SideOverlay: FC<SideOverlayProps> = ({
  isOpen,
  title,
  body,
  onClose,
  className,
  hasSideNav,
  style,
  dataset,
  'data-testid': dataTestId,
  direction = 'right',
}) => {
  useMotifStyles(MotifComponent.SIDE_OVERLAY);

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        closeButtonRef?.current?.focus();
      }, 250);
    }
  }, [isOpen]);

  const handleBackdropClick = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
  ) => {
    onClose?.(event);
  };

  const isLeft = direction === 'left';

  return (
    <DirectionalOverlay
      backdropClassName={sideOverlayBackdropMobileOnlyCss}
      showBackdrop
      className={cx(
        MotifComponent.SIDE_OVERLAY,
        sideOverlayCss,
        isLeft ? sideOverlayDirectionLeftCss : sideOverlayDirectionRightCss,
        {
          [sideOverlayWithSideNavCss]: hasSideNav && !isLeft,
          [sideOverlayWithSideNavDirectionLeftCss]: hasSideNav && isLeft,
        },
        className
      )}
      dataset={dataset}
      data-testid={dataTestId}
      desktopDirection={direction}
      desktopSizePercent={sideOverlayDesktopSizePercent}
      isOpen={isOpen}
      mobileDirection="bottom"
      mobileSizePercent={sideOverlayMobileSizePercent}
      onBackdropClick={handleBackdropClick}
      style={style}
    >
      <div className={sideOverlayHeaderCss}>
        <button
          className={sideOverlayCloseButtonCss}
          type="button"
          ref={closeButtonRef}
          onClick={onClose}
          onKeyDown={onClose}
        >
          <Icon name="cross" size={24} fill={m('--side-overlay-fg-color')} />
        </button>
      </div>
      <dl className={sideOverlayListCss}>
        <dt className={sideOverlayTermCss}>{title}</dt>
        <dd className={sideOverlayDefinitionCss}>{body}</dd>
      </dl>
    </DirectionalOverlay>
  );
};

SideOverlay.displayName = 'SideOverlay';
